import client from './client';
import { fetchAllPages } from './fetchAllPages';
import { extractCollectionResponse } from './collectionResponse';

function trimOrNull(value) {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  return text ? text : null;
}

function toNumberOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''));
  return Number.isNaN(parsed) ? null : parsed;
}

function normalizeDataset(row = {}) {
  return {
    uuid: row.uuid ?? null,
    id: row.uuid ?? row.id ?? null,
    dataset_name: row.dataset_name ?? '',
    source_organisation: row.source_organisation ?? '',
    indicator_reference: row.indicator_reference ?? null,
    reference_period: row.reference_period ?? '',
    geographic_scope: row.geographic_scope ?? '',
    state: row.state ?? null,
    lga: row.lga ?? null,
    value_numeric: toNumberOrNull(row.value_numeric),
    notes: row.notes ?? null,
    data_status: row.data_status ?? 'draft',
    submitted_by: row.submitted_by ?? null,
    submitted_at: row.submitted_at ?? null,
    validated_by: row.validated_by ?? null,
    validated_at: row.validated_at ?? null,
    approved_by: row.approved_by ?? null,
    approved_at: row.approved_at ?? null,
    rejected_at: row.rejected_at ?? null,
    rejection_notes: row.rejection_notes ?? null,
    created_at: row.created_at ?? null,
    updated_at: row.updated_at ?? null,
  };
}

function unwrapResponse(responseData) {
  return responseData?.data ?? responseData ?? {};
}

function extractCollection(responseData) {
  return extractCollectionResponse(responseData, normalizeDataset);
}

function extractItem(responseData, targetUuid) {
  const payload = unwrapResponse(responseData);
  const candidates = [payload, payload?.data];

  for (const candidate of candidates) {
    if (!candidate) continue;

    if (Array.isArray(candidate.items)) {
      const match = targetUuid
        ? candidate.items.find((item) => item?.uuid === targetUuid)
        : candidate.items[0];

      if (match) return normalizeDataset(match);
    }

    if (candidate.uuid || candidate.dataset_name) {
      return normalizeDataset(candidate);
    }
  }

  return normalizeDataset({});
}

function buildPayload(payload = {}) {
  return {
    dataset_name: trimOrNull(payload.dataset_name),
    source_organisation: trimOrNull(payload.source_organisation),
    indicator_reference: trimOrNull(payload.indicator_reference),
    reference_period: trimOrNull(payload.reference_period),
    geographic_scope: trimOrNull(payload.geographic_scope),
    state: trimOrNull(payload.state),
    lga: trimOrNull(payload.lga),
    value_numeric: toNumberOrNull(payload.value_numeric),
    notes: trimOrNull(payload.notes),
  };
}

function buildQueryParams(params = {}) {
  const query = {
    per_page: params.per_page ?? 15,
    page: params.page ?? 1,
  };

  Object.entries(params).forEach(([key, value]) => {
    if (key === 'per_page' || key === 'page') return;
    if (value === null || value === undefined) return;

    const text = String(value).trim();
    if (text) query[key] = text;
  });

  return query;
}

async function fetchDatasetPage(params = {}) {
  const { data } = await client.get('/v1/datasets', {
    params: buildQueryParams(params),
  });
  return extractCollection(data);
}

async function fetchList(path, params = {}) {
  const { data } = await client.get(path, { params });
  const payload = data?.data ?? data ?? {};
  const items =
    Array.isArray(payload) ? payload :
    Array.isArray(payload.items) ? payload.items :
    Array.isArray(payload.results) ? payload.results :
    Array.isArray(payload.data) ? payload.data :
    [];
  return items;
}

export const dataSourcesApi = {
  async list() {
    try {
      const items = await fetchList('/v1/data-sources', { per_page: 500 });
      return items.map((item) => ({
        value: item.name ?? item.code ?? item.uuid,
        label: item.name ?? item.code ?? item.uuid,
      })).filter((item) => item.value);
    } catch {
      return [];
    }
  },
};

export const indicatorsApi = {
  async list() {
    try {
      const items = await fetchList('/v1/indicators', { per_page: 500 });
      return items.map((item) => ({
        value: item.code ?? item.name ?? item.uuid,
        label: item.name ?? item.code ?? item.uuid,
      })).filter((item) => item.value);
    } catch {
      return [];
    }
  },
};

export const supplementaryDatasetApi = {
  async list(params = {}) {
    return fetchDatasetPage(params);
  },

  async listAll(params = {}) {
    return fetchAllPages((page) => fetchDatasetPage({ ...params, page }));
  },

  async get(uuid) {
    const { data } = await client.get(`/v1/datasets/${uuid}`);
    return extractItem(data, uuid);
  },

  async create(payload) {
    const { data } = await client.post('/v1/datasets', buildPayload(payload));
    return extractItem(data);
  },

  async update(uuid, payload) {
    const { data } = await client.put(`/v1/datasets/${uuid}`, buildPayload(payload));
    return extractItem(data, uuid);
  },

  async remove(uuid) {
    const { data } = await client.delete(`/v1/datasets/${uuid}`);
    return data;
  },

  async submit(uuid) {
    const { data } = await client.post(`/v1/datasets/${uuid}/submit`);
    return extractItem(data, uuid);
  },

  async validate(uuid) {
    const { data } = await client.post(`/v1/datasets/${uuid}/validate`);
    return extractItem(data, uuid);
  },

  async approve(uuid) {
    const { data } = await client.post(`/v1/datasets/${uuid}/approve`);
    return extractItem(data, uuid);
  },

  async reject(uuid, rejectionNotes) {
    const { data } = await client.post(`/v1/datasets/${uuid}/reject`, {
      rejection_notes: trimOrNull(rejectionNotes),
    });
    return extractItem(data, uuid);
  },
};

export default supplementaryDatasetApi;
export { extractCollection, fetchDatasetPage };
